import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { projectsReducer } from '../../store/reducers/projects.reducer';
import { toastReducer } from '../../store/reducers/toast.reducer';
import { getUser } from '../../store/selectors/user.selector';
import { buildCadProjectForm } from '../new-project-cad/cadProjectRequest';
import { CadPlan } from '../new-project-cad/useCadState';
import { renderPlanThumbnail } from './planThumbnail';

interface CreateProjectFromDrawingDialogProps {
  defaultName: string;
  plan: CadPlan & { imageScale: number | null };
  referenceImageUrl: string | null;
  /** Saves the drawing first (creating it if it is new), so the project gets what is saved; resolves to its id, null when that failed. */
  saveDrawing: () => Promise<number | null>;
  onCancel: () => void;
}

/** Asks only for the name; the project opens in the workspace with its own copy of the model. */
export function CreateProjectFromDrawingDialog({
  defaultName,
  plan,
  referenceImageUrl,
  saveDrawing,
  onCancel,
}: CreateProjectFromDrawingDialogProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(getUser)?.settings;
  const [name, setName] = useState(defaultName);
  const [creating, setCreating] = useState(false);

  const create = async () => {
    setCreating(true);
    try {
      const drawingId = await saveDrawing();
      if (drawingId === null) throw new Error('The drawing could not be saved');
      const form = await buildCadProjectForm({
        name: name.trim() || defaultName,
        plan,
        imageUrl: referenceImageUrl,
        imageScale: plan.imageScale,
        previewBase64: renderPlanThumbnail(plan.polygons),
        settings,
        sourceDrawingId: drawingId,
      });
      // Opens the new project in the workspace when it is created.
      dispatch(projectsReducer.actions.createProject(form));
    } catch (error) {
      console.error('Creating a project from the drawing failed:', error);
      dispatch(toastReducer.actions.showToast({ message: 'drawings.createProjectFailed', type: 'error' }));
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/60"
      onClick={creating ? undefined : onCancel}
    >
      <div
        className="w-[400px] max-w-[calc(100vw-32px)] rounded-2xl bg-[#232323] border border-white/10 shadow-2xl p-6 text-gray-300"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-white mb-4">{t('drawings.createProjectTitle')}</h2>
        <input
          type="text"
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !creating && create()}
          placeholder={t('projects.newProject.projectNamePlaceholder')}
          maxLength={255}
          className="w-full h-[36px] rounded-lg bg-black border border-black focus:border-lime-400 focus:ring-2 focus:ring-lime-400/30 placeholder-[#747474] text-sm px-4 text-white transition outline-none"
        />
        <p className="mt-3 text-xs text-gray-500">{t('drawings.createProjectHint')}</p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={creating}
            className="h-9 px-4 rounded-full text-xs font-medium border border-white/20 text-gray-200 hover:border-white/40 disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={create}
            disabled={creating}
            className="h-9 px-4 rounded-full text-xs font-medium bg-lime-400 text-black hover:bg-lime-500 disabled:opacity-50"
          >
            {creating ? t('projects.cadProject.creating') : t('drawings.createProject')}
          </button>
        </div>
      </div>
    </div>
  );
}
